import { useState, useEffect } from 'react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/toastContext';
import { useConfirm } from '../components/ui/useConfirm';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  is_active: boolean;
  max_users: number | null;
  max_agents: number | null;
  max_sandboxes: number | null;
  pool_size: number | null;
  max_cpu: number | null;
  max_memory_mb: number | null;
  max_disk_mb: number | null;
  allowed_frameworks: string[] | null;
  created_at: string;
  updated_at: string;
}

/** 提取后端错误文案（detail 优先，其次 Error.message） */
const errorMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (typeof err.message === 'string' && err.message) return err.message;
  return '未知错误';
};

export default function TenantAdmin() {
  const toast = useToast();
  const { confirm, dialog } = useConfirm();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState({
    name: '',
    slug: '',
    max_users: '50',
    max_agents: '10',
    max_sandboxes: '20',
    pool_size: '3',
    max_cpu: '10',
    max_memory_mb: '10240',
    max_disk_mb: '102400',
    allowed_frameworks: '',
  });
  const [editForm, setEditForm] = useState({
    name: '',
    is_active: true,
    max_users: '50',
    max_agents: '10',
    max_sandboxes: '20',
    pool_size: '3',
    max_cpu: '10',
    max_memory_mb: '10240',
    max_disk_mb: '102400',
    allowed_frameworks: '',
  });

  const loadTenants = async () => {
    try {
      const res = await apiClient.get('/tenants');
      setTenants(res.data);
    } catch (e) {
      console.error('Failed to load tenants:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState 闭包
    const tick = () => {
      void loadTenants();
    };
    tick();
  }, []);

  const createTenant = async () => {
    if (!createForm.name.trim() || !createForm.slug.trim()) {
      toast.error('名称和 Slug 为必填项');
      return;
    }
    try {
      const res = await apiClient.post('/tenants', {
        name: createForm.name,
        slug: createForm.slug,
        max_users: parseInt(createForm.max_users) || 50,
        max_agents: parseInt(createForm.max_agents) || 10,
        max_sandboxes: parseInt(createForm.max_sandboxes) || 20,
        pool_size: parseInt(createForm.pool_size) || 3,
        max_cpu: parseFloat(createForm.max_cpu) || 10,
        max_memory_mb: parseInt(createForm.max_memory_mb) || 10240,
        max_disk_mb: parseInt(createForm.max_disk_mb) || 102400,
        allowed_frameworks: createForm.allowed_frameworks
          ? createForm.allowed_frameworks.split(',').map((s: string) => s.trim()).filter(Boolean)
          : undefined,
      });
      setTenants([res.data, ...tenants]);
      setShowCreate(false);
      setCreateForm({ name: '', slug: '', max_users: '50', max_agents: '10', max_sandboxes: '20', pool_size: '3', max_cpu: '10', max_memory_mb: '10240', max_disk_mb: '102400', allowed_frameworks: '' });
    } catch (e) {
      toast.error('创建失败: ' + errorMessage(e));
    }
  };

  const startEdit = (tenant: Tenant) => {
    setEditId(tenant.id);
    setEditForm({
      name: tenant.name,
      is_active: tenant.is_active,
      max_users: tenant.max_users != null ? String(tenant.max_users) : '',
      max_agents: tenant.max_agents != null ? String(tenant.max_agents) : '',
      max_sandboxes: tenant.max_sandboxes != null ? String(tenant.max_sandboxes) : '',
      pool_size: tenant.pool_size != null ? String(tenant.pool_size) : '',
      max_cpu: tenant.max_cpu != null ? String(tenant.max_cpu) : '',
      max_memory_mb: tenant.max_memory_mb != null ? String(tenant.max_memory_mb) : '',
      max_disk_mb: tenant.max_disk_mb != null ? String(tenant.max_disk_mb) : '',
      allowed_frameworks: (tenant.allowed_frameworks || []).join(', '),
    });
  };

  const saveEdit = async (id: string) => {
    try {
      const res = await apiClient.patch(`/tenants/${id}`, {
        name: editForm.name,
        is_active: editForm.is_active,
        max_users: parseInt(editForm.max_users) || 50,
        max_agents: parseInt(editForm.max_agents) || 10,
        max_sandboxes: parseInt(editForm.max_sandboxes) || 20,
        pool_size: parseInt(editForm.pool_size) || 3,
        max_cpu: parseFloat(editForm.max_cpu) || 10,
        max_memory_mb: parseInt(editForm.max_memory_mb) || 10240,
        max_disk_mb: parseInt(editForm.max_disk_mb) || 102400,
        allowed_frameworks: editForm.allowed_frameworks
          ? editForm.allowed_frameworks.split(',').map((s: string) => s.trim()).filter(Boolean)
          : undefined,
      });
      setTenants(tenants.map(t => (t.id === id ? res.data : t)));
      setEditId(null);
    } catch (e) {
      toast.error('更新失败: ' + errorMessage(e));
    }
  };

  const toggleActive = async (tenant: Tenant) => {
    try {
      const res = await apiClient.patch(`/tenants/${tenant.id}`, {
        is_active: !tenant.is_active,
      });
      setTenants(tenants.map(t => (t.id === tenant.id ? res.data : t)));
    } catch (e) {
      toast.error('操作失败: ' + errorMessage(e));
    }
  };

  const deleteTenant = async (tenant: Tenant) => {
    if (
      !(await confirm({
        title: '确定要删除这个 Tenant 吗？',
        message: '此操作不可恢复。',
        danger: true,
        requireText: tenant.slug,
        confirmText: '删除',
      }))
    )
      return;
    try {
      await apiClient.delete(`/tenants/${tenant.id}`);
      setTenants(tenants.filter(t => t.id !== tenant.id));
    } catch (e) {
      toast.error('删除失败: ' + errorMessage(e));
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="flex items-center justify-between mb-4">
        <h1>Tenant Admin</h1>
        <button className="primary" onClick={() => setShowCreate(!showCreate)}>
          + 新建 Tenant
        </button>
      </div>

      {showCreate && (
        <div className="card mb-4">
          <h3>创建新 Tenant</h3>
          <div className="flex flex-col gap-3">
            <input
              value={createForm.name}
              onChange={e => setCreateForm({ ...createForm, name: e.target.value })}
              placeholder="Tenant 名称 *"
            />
            <input
              value={createForm.slug}
              onChange={e => setCreateForm({ ...createForm, slug: e.target.value })}
              placeholder="Slug（唯一标识）*"
            />
            <div className="flex gap-4">
              <div style={{ flex: 1 }}>
                <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>最大用户数</label>
                <input
                  type="number"
                  value={createForm.max_users}
                  onChange={e => setCreateForm({ ...createForm, max_users: e.target.value })}
                  placeholder="50"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>最大 Agent 数</label>
                <input
                  type="number"
                  value={createForm.max_agents}
                  onChange={e => setCreateForm({ ...createForm, max_agents: e.target.value })}
                  placeholder="10"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <div style={{ flex: 1 }}>
                <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>最大沙箱数</label>
                <input
                  type="number"
                  value={createForm.max_sandboxes}
                  onChange={e => setCreateForm({ ...createForm, max_sandboxes: e.target.value })}
                  placeholder="20"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>池大小</label>
                <input
                  type="number"
                  value={createForm.pool_size}
                  onChange={e => setCreateForm({ ...createForm, pool_size: e.target.value })}
                  placeholder="3"
                />
              </div>
            </div>
            <div className="flex gap-4">
              <div style={{ flex: 1 }}>
                <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>最大 CPU (核)</label>
                <input
                  type="number"
                  step="0.1"
                  value={createForm.max_cpu}
                  onChange={e => setCreateForm({ ...createForm, max_cpu: e.target.value })}
                  placeholder="10"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>最大内存 (MB)</label>
                <input
                  type="number"
                  value={createForm.max_memory_mb}
                  onChange={e => setCreateForm({ ...createForm, max_memory_mb: e.target.value })}
                  placeholder="10240"
                />
              </div>
              <div style={{ flex: 1 }}>
                <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>最大磁盘 (MB)</label>
                <input
                  type="number"
                  value={createForm.max_disk_mb}
                  onChange={e => setCreateForm({ ...createForm, max_disk_mb: e.target.value })}
                  placeholder="102400"
                />
              </div>
            </div>
            <input
              value={createForm.allowed_frameworks}
              onChange={e => setCreateForm({ ...createForm, allowed_frameworks: e.target.value })}
              placeholder="允许的框架（逗号分隔，可选）：celestial_toad, hermes"
            />
            <div className="flex gap-2">
              <button className="primary" onClick={createTenant}>创建</button>
              <button className="secondary" onClick={() => setShowCreate(false)}>取消</button>
            </div>
          </div>
        </div>
      )}

      {tenants.length === 0 ? (
        <div className="empty">
          <h3>暂无 Tenant</h3>
          <p>点击"新建 Tenant"开始创建</p>
        </div>
      ) : (
        <div className="list">
          {tenants.map(tenant => (
            <div key={tenant.id} className="list-item">
              {editId === tenant.id ? (
                <div className="list-item-content" style={{ width: '100%' }}>
                  <div className="flex flex-col gap-3">
                    <input
                      value={editForm.name}
                      onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                      placeholder="名称"
                    />
                    <div className="flex gap-4">
                      <div style={{ flex: 1 }}>
                        <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>最大用户数</label>
                        <input
                          type="number"
                          value={editForm.max_users}
                          onChange={e => setEditForm({ ...editForm, max_users: e.target.value })}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>最大 Agent 数</label>
                        <input
                          type="number"
                          value={editForm.max_agents}
                          onChange={e => setEditForm({ ...editForm, max_agents: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div style={{ flex: 1 }}>
                        <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>最大沙箱数</label>
                        <input
                          type="number"
                          value={editForm.max_sandboxes}
                          onChange={e => setEditForm({ ...editForm, max_sandboxes: e.target.value })}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>池大小</label>
                        <input
                          type="number"
                          value={editForm.pool_size}
                          onChange={e => setEditForm({ ...editForm, pool_size: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div style={{ flex: 1 }}>
                        <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>最大 CPU (核)</label>
                        <input
                          type="number"
                          step="0.1"
                          value={editForm.max_cpu}
                          onChange={e => setEditForm({ ...editForm, max_cpu: e.target.value })}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>最大内存 (MB)</label>
                        <input
                          type="number"
                          value={editForm.max_memory_mb}
                          onChange={e => setEditForm({ ...editForm, max_memory_mb: e.target.value })}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <label className="text-sm text-muted" style={{ display: 'block', marginBottom: 4 }}>最大磁盘 (MB)</label>
                        <input
                          type="number"
                          value={editForm.max_disk_mb}
                          onChange={e => setEditForm({ ...editForm, max_disk_mb: e.target.value })}
                        />
                      </div>
                    </div>
                    <input
                      value={editForm.allowed_frameworks}
                      onChange={e => setEditForm({ ...editForm, allowed_frameworks: e.target.value })}
                      placeholder="允许的框架（逗号分隔）"
                    />
                    <div className="flex gap-2">
                      <button className="primary" onClick={() => saveEdit(tenant.id)}>保存</button>
                      <button className="secondary" onClick={() => setEditId(null)}>取消</button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="list-item-content">
                    <div className="flex items-center gap-2">
                      <h3 style={{ margin: 0 }}>{tenant.name}</h3>
                      <span className={`badge ${tenant.is_active ? 'success' : 'error'}`}>
                        {tenant.is_active ? '活跃' : '停用'}
                      </span>
                    </div>
                    <p style={{ margin: '4px 0 0' }}>
                      <span className="text-muted">Slug: {tenant.slug}</span>
                      <span className="text-muted"> · 用户: {tenant.max_users ?? '不限'}</span>
                      <span className="text-muted"> · Agent: {tenant.max_agents ?? '不限'}</span>
                      <span className="text-muted"> · 沙箱: {tenant.max_sandboxes ?? '不限'}</span>
                      <span className="text-muted"> · 池: {tenant.pool_size ?? '不限'}</span>
                      <span className="text-muted"> · CPU: {tenant.max_cpu != null ? `${tenant.max_cpu}核` : '不限'}</span>
                      <span className="text-muted"> · 内存: {tenant.max_memory_mb != null ? `${tenant.max_memory_mb}MB` : '不限'}</span>
                      <span className="text-muted"> · 磁盘: {tenant.max_disk_mb != null ? `${tenant.max_disk_mb}MB` : '不限'}</span>
                      {tenant.allowed_frameworks && tenant.allowed_frameworks.length > 0 && (
                        <span className="text-muted"> · 框架: {tenant.allowed_frameworks.join(', ')}</span>
                      )}
                    </p>
                    <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>
                      创建于: {new Date(tenant.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="list-item-actions" style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
                    <button className="secondary" onClick={() => toggleActive(tenant)} style={{ padding: '6px 12px' }}>
                      {tenant.is_active ? '停用' : '启用'}
                    </button>
                    <button className="secondary" onClick={() => startEdit(tenant)} style={{ padding: '6px 12px' }}>
                      编辑
                    </button>
                    <button className="secondary" onClick={() => deleteTenant(tenant)} style={{ padding: '6px 12px' }}>
                      删除
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {dialog}
    </div>
  );
}
